import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Grid,
  IconButton,
  MenuItem,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/DeleteOutline";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import ErrorOutlineIcon from "@mui/icons-material/ErrorOutline";
import {
  providersApi,
  type ProviderConfig,
  type ProviderKeyStatus,
  type ProviderName,
} from "../api/providers";

const PROVIDERS: ProviderName[] = ["brevo", "resend"];

interface DraftConfig {
  domain: string;
  primary: ProviderName;
  fallback: ProviderName | "";
  fromAddressDefault: string;
}

const EMPTY: DraftConfig = { domain: "", primary: "brevo", fallback: "", fromAddressDefault: "" };

type KeyKind = "apiKey" | "webhookSecret";

interface KeyDraft {
  provider: ProviderName;
  kind: KeyKind;
  value: string;
}

function relativeTime(iso?: string): string {
  if (!iso) return "—";
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return "—";
  const diff = Date.now() - t;
  const sec = Math.round(diff / 1000);
  if (sec < 60) return "just now";
  const min = Math.round(sec / 60);
  if (min < 60) return `${min}m ago`;
  const hr = Math.round(min / 60);
  if (hr < 24) return `${hr}h ago`;
  const day = Math.round(hr / 24);
  if (day < 30) return `${day}d ago`;
  const mo = Math.round(day / 30);
  if (mo < 12) return `${mo}mo ago`;
  return `${Math.round(mo / 12)}y ago`;
}

function kindLabel(kind: KeyKind): string {
  return kind === "apiKey" ? "API key" : "Webhook secret";
}

function ssmPath(provider: ProviderName, kind: KeyKind): string {
  return `/imera/${provider}/${kind === "apiKey" ? "api-key" : "webhook-secret"}`;
}

export function ProvidersPage() {
  const [providers, setProviders] = useState<ProviderConfig[]>([]);
  const [keys, setKeys] = useState<ProviderKeyStatus[]>([]);
  const [editing, setEditing] = useState<DraftConfig | null>(null);
  const [isNew, setIsNew] = useState(false);
  const [keyDraft, setKeyDraft] = useState<KeyDraft | null>(null);
  const [savingKey, setSavingKey] = useState(false);
  const [keyError, setKeyError] = useState<string | null>(null);

  async function refresh() {
    const [pRes, kRes] = await Promise.all([providersApi.list(), providersApi.listKeys()]);
    setProviders(pRes.providers);
    setKeys(kRes.providers);
  }

  useEffect(() => {
    refresh();
  }, []);

  async function save() {
    if (!editing) return;
    await providersApi.upsert(editing.domain.toLowerCase(), {
      primary: editing.primary,
      fallback: editing.fallback === "" ? null : editing.fallback,
      fromAddressDefault: editing.fromAddressDefault || undefined,
    });
    setEditing(null);
    setIsNew(false);
    await refresh();
  }

  async function remove(domain: string) {
    if (!confirm(`Delete provider config for ${domain}? Sends from this domain will fail.`)) return;
    await providersApi.remove(domain);
    await refresh();
  }

  async function saveKey() {
    if (!keyDraft || !keyDraft.value.trim()) return;
    setSavingKey(true);
    setKeyError(null);
    try {
      await providersApi.updateKeys(keyDraft.provider, {
        [keyDraft.kind]: keyDraft.value.trim(),
      });
      setKeyDraft(null);
      await refresh();
    } catch (err) {
      setKeyError(err instanceof Error ? err.message : "Failed to save key");
    } finally {
      setSavingKey(false);
    }
  }

  function statusFor(provider: ProviderName): ProviderKeyStatus | undefined {
    return keys.find((k) => k.provider === provider);
  }

  return (
    <Box>
      <Stack direction="row" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h4">Providers</Typography>
        <Button
          variant="contained"
          onClick={() => {
            setEditing(EMPTY);
            setIsNew(true);
          }}
        >
          Add domain
        </Button>
      </Stack>

      <Typography variant="overline" color="text.secondary">
        Provider credentials
      </Typography>
      <Grid container spacing={2} sx={{ mt: 0.5, mb: 2 }}>
        {PROVIDERS.map((provider) => {
          const status = statusFor(provider);
          return (
            <Grid item xs={12} md={6} key={provider}>
              <Card variant="outlined">
                <CardContent>
                  <Typography variant="h6" gutterBottom sx={{ textTransform: "capitalize" }}>
                    {provider}
                  </Typography>
                  <Stack spacing={1.5}>
                    {(["apiKey", "webhookSecret"] as KeyKind[]).map((kind) => {
                      const set =
                        kind === "apiKey" ? !!status?.apiKeySet : !!status?.webhookSecretSet;
                      const lastModified =
                        kind === "apiKey"
                          ? status?.apiKeyLastModified
                          : status?.webhookSecretLastModified;
                      return (
                        <Stack
                          key={kind}
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={2}
                        >
                          <Box>
                            <Typography variant="body2">{kindLabel(kind)}</Typography>
                            <Stack direction="row" spacing={0.75} alignItems="center" sx={{ mt: 0.25 }}>
                              {set ? (
                                <CheckCircleOutlineIcon fontSize="small" color="success" />
                              ) : (
                                <ErrorOutlineIcon fontSize="small" color="warning" />
                              )}
                              <Typography variant="caption" color="text.secondary">
                                {set ? `Set · ${relativeTime(lastModified)}` : "Not set"}
                              </Typography>
                            </Stack>
                            <Typography
                              variant="caption"
                              color="text.secondary"
                              component="div"
                              sx={{ fontFamily: "monospace", mt: 0.25 }}
                            >
                              {ssmPath(provider, kind)}
                            </Typography>
                          </Box>
                          <Button
                            size="small"
                            variant={set ? "outlined" : "contained"}
                            onClick={() => {
                              setKeyError(null);
                              setKeyDraft({ provider, kind, value: "" });
                            }}
                          >
                            {set ? "Update" : "Set"}
                          </Button>
                        </Stack>
                      );
                    })}
                  </Stack>
                </CardContent>
              </Card>
            </Grid>
          );
        })}
      </Grid>
      <Alert severity="info" sx={{ mb: 4 }}>
        Updated keys take effect on the next Lambda cold start (~15 min idle, or sooner under load).
        To force-refresh sooner, push any backend change to recycle the send Lambdas.
      </Alert>

      <Typography variant="overline" color="text.secondary">
        Domain routing
      </Typography>
      <Card sx={{ mt: 0.5 }}>
        <CardContent>
          {providers.length === 0 ? (
            <Typography color="text.secondary">
              No domains configured. Until you add one, all sends will fail with{" "}
              <code>unknown_from_domain</code>.
            </Typography>
          ) : (
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>Domain</TableCell>
                  <TableCell>Primary</TableCell>
                  <TableCell>Fallback</TableCell>
                  <TableCell>Default from</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {providers.map((p) => (
                  <TableRow key={p.domain}>
                    <TableCell>
                      <code>{p.domain}</code>
                    </TableCell>
                    <TableCell>{p.primary}</TableCell>
                    <TableCell>{p.fallback ?? "—"}</TableCell>
                    <TableCell>{p.fromAddressDefault ?? "—"}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        size="small"
                        onClick={() => {
                          setEditing({
                            domain: p.domain,
                            primary: p.primary,
                            fallback: p.fallback ?? "",
                            fromAddressDefault: p.fromAddressDefault ?? "",
                          });
                          setIsNew(false);
                        }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                      <IconButton size="small" onClick={() => remove(p.domain)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      <Dialog open={!!editing} onClose={() => setEditing(null)} fullWidth maxWidth="sm">
        <DialogTitle>{isNew ? "Add domain" : `Edit ${editing?.domain}`}</DialogTitle>
        <DialogContent>
          {editing && (
            <Stack spacing={2} mt={1}>
              <TextField
                label="Domain"
                value={editing.domain}
                onChange={(e) => setEditing({ ...editing, domain: e.target.value })}
                disabled={!isNew}
                helperText="e.g. techlead.co.nz"
              />
              <TextField
                select
                label="Primary provider"
                value={editing.primary}
                onChange={(e) =>
                  setEditing({ ...editing, primary: e.target.value as ProviderName })
                }
              >
                {PROVIDERS.map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                select
                label="Fallback provider"
                value={editing.fallback}
                onChange={(e) =>
                  setEditing({
                    ...editing,
                    fallback: e.target.value as ProviderName | "",
                  })
                }
                helperText="(none) means no retry on a different provider"
              >
                <MenuItem value="">(none)</MenuItem>
                {PROVIDERS.filter((p) => p !== editing.primary).map((p) => (
                  <MenuItem key={p} value={p}>
                    {p}
                  </MenuItem>
                ))}
              </TextField>
              <TextField
                label="Default from address (optional)"
                value={editing.fromAddressDefault}
                onChange={(e) =>
                  setEditing({ ...editing, fromAddressDefault: e.target.value })
                }
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditing(null)}>Cancel</Button>
          <Button variant="contained" onClick={save} disabled={!editing?.domain}>
            Save
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={!!keyDraft}
        onClose={() => (savingKey ? null : setKeyDraft(null))}
        fullWidth
        maxWidth="sm"
      >
        <DialogTitle>
          {keyDraft &&
            `${statusFor(keyDraft.provider) &&
            (keyDraft.kind === "apiKey"
              ? statusFor(keyDraft.provider)?.apiKeySet
              : statusFor(keyDraft.provider)?.webhookSecretSet)
              ? "Update"
              : "Set"} ${keyDraft.provider} ${kindLabel(keyDraft.kind).toLowerCase()}`}
        </DialogTitle>
        <DialogContent>
          {keyDraft && (
            <Stack spacing={2} mt={1}>
              <Typography variant="body2" color="text.secondary">
                Stored as a SecureString in AWS SSM at{" "}
                <code>{ssmPath(keyDraft.provider, keyDraft.kind)}</code>. The value is never
                returned to the dashboard after saving.
              </Typography>
              {keyError && <Alert severity="error">{keyError}</Alert>}
              <TextField
                label={kindLabel(keyDraft.kind)}
                type="password"
                value={keyDraft.value}
                onChange={(e) => setKeyDraft({ ...keyDraft, value: e.target.value })}
                fullWidth
                autoFocus
                autoComplete="off"
                placeholder={
                  keyDraft.kind === "apiKey"
                    ? keyDraft.provider === "brevo"
                      ? "xkeysib-…"
                      : "re_…"
                    : "whsec_… or provider-issued secret"
                }
              />
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setKeyDraft(null)} disabled={savingKey}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={saveKey}
            disabled={savingKey || !keyDraft?.value.trim()}
          >
            {savingKey ? "Saving…" : "Save"}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
